import { FC, useState } from 'react'
import * as Yup from 'yup'
import { Select } from 'antd'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/auth-context'
import { FullMeetingFragment, ObjectivePayment, ObjectivePaymentInput, ObjectivePaymentManualDeductionReason } from '../../types'
import { PerModal } from '../../components/custom_components/Modal'
import { ValidationErrors } from '../../components/custom_components/ValidationErrors'
import { InputNumber, FormItem, FormikSelect } from '../../components/formik-component'
import PerSpacer from '../../components/custom_components/Spacer'
import useSaveOrUpdateObjectivePayment from '../../graphql/objectivePayments/hooks/useSaveOrUpdateObjectivePayment'
import PerText from '../../components/custom_components/Text'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'
import { useCurrency } from '../../context/currency-context'

interface ManualDeductionModalProps {
  visible: boolean
  closeModal: () => void
  meeting: FullMeetingFragment
  targetBonusToBePaid: number
  objectivePayment: Pick<ObjectivePayment, 'id' | 'meetingId' | 'deduction' | 'deductionReason' | 'paymentDate'>
}

export const ManualDeductionModal: FC<ManualDeductionModalProps> = ({
  meeting,
  objectivePayment,
  visible,
  closeModal,
  targetBonusToBePaid,
}) => {
  const { Option } = Select
  const { user } = useAuth()
  const { formatCurrencySymbol } = useAmountAndCurrency(user.locale)
  const { currency } = useCurrency()
  const [t] = useTranslation()
  const [serverError, setServerError] = useState(null)
  const saveOrUpdateObjectivePayment = useSaveOrUpdateObjectivePayment(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.pastTargetsReferenceYear,
  )

  // save updates
  const updateObjectivePayment = async (values: ObjectivePaymentInput) => {
    const update = await saveOrUpdateObjectivePayment.createOrUpdateObjectivePayment(values)
    if (update && !update.errors) {
      setServerError(null)
      closeModal()
    } else {
      setServerError(update.errors && update.errors[0] && update.errors[0].extensions ? update.errors[0].extensions.validationErrors : null)
    }
  }

  return (
    <Formik
      initialValues={{
        id: objectivePayment.id,
        meetingId: objectivePayment.meetingId,
        deduction: objectivePayment.deduction,
        deductionReason: objectivePayment.deductionReason,
        paymentDate: objectivePayment.paymentDate,
      }}
      enableReinitialize={true}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        updateObjectivePayment(values)
        closeModal()
        resetForm()
        setSubmitting(false)
      }}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={Yup.object().shape({
        deduction: Yup.number()
          .moreThan(
            0,
            t('formError.minValue', {
              fieldName: t('appraisalMeeting.stepPayment.deduction.modal.amount'),
              minAmount: 0,
              currency: formatCurrencySymbol(currency),
            }),
          )
          .max(
            targetBonusToBePaid,
            t('formError.maxValue', { fieldName: t('appraisalMeeting.stepPayment.deduction.modal.amount'), amount: targetBonusToBePaid }),
          )
          .required(t('formError.required', { fieldName: t('appraisalMeeting.stepPayment.deduction.modal.amount') }))
          .nullable(),
        deductionReason: Yup.string()
          .required(t('formError.required', { fieldName: t('appraisalMeeting.stepPayment.deduction.modal.reason') }))
          .nullable(),
      })}
    >
      {(props) => {
        const { isSubmitting, handleSubmit } = props
        return (
          <PerModal
            visible={visible}
            onCancel={closeModal}
            title={{ heading: t(`appraisalMeeting.stepPayment.deduction.modal.add`) }}
            footer={{
              formikForm: {
                onSubmit: handleSubmit,
              },
              confirmBtn: {
                text: 'save',
                disabled: isSubmitting,
                isProcessing: { active: isSubmitting },
              },
            }}
          >
            <>
              {serverError && <ValidationErrors errors={serverError} />}
              <div data-e2e-payment-deduction-container>
                <PerText appearance="info" mb="l">
                  {t('appraisalMeeting.stepPayment.deduction.modal.hint')}
                </PerText>

                <PerSpacer mb="xs">
                  <PerText appearance="label-small" display="block">
                    {t('appraisalMeeting.stepPayment.deduction.modal.amount')}
                  </PerText>
                  <FormItem name="deduction">
                    <InputNumber
                      data-e2e-deduction-input
                      name="deduction"
                      type="currency"
                      currency={currency}
                      timezoneAndLocale={user}
                      step={50}
                      min={0}
                      placeholder="0"
                    />
                  </FormItem>
                </PerSpacer>

                <div>
                  <PerText appearance="label-small" display="block">
                    {t('appraisalMeeting.stepPayment.deduction.modal.reason')}
                  </PerText>
                  <FormItem name="deductionReason">
                    <FormikSelect
                      data-e2e-deduction-reason-dropdown
                      name="deductionReason"
                      placeholder={t('appraisalMeeting.stepPayment.deduction.reason.placeholder')}
                      style={{ width: 250 }}
                    >
                      <Option value={ObjectivePaymentManualDeductionReason.Sabatical} data-e2e-deduction-reason-dropdown-value>
                        {t(`appraisalMeeting.stepPayment.deduction.reason.${ObjectivePaymentManualDeductionReason.Sabatical}`)}
                      </Option>
                      <Option value={ObjectivePaymentManualDeductionReason.LongTermSickness}>
                        {t(`appraisalMeeting.stepPayment.deduction.reason.${ObjectivePaymentManualDeductionReason.LongTermSickness}`)}
                      </Option>
                      <Option value={ObjectivePaymentManualDeductionReason.ParentalLeave}>
                        {t(`appraisalMeeting.stepPayment.deduction.reason.${ObjectivePaymentManualDeductionReason.ParentalLeave}`)}
                      </Option>
                      <Option value={ObjectivePaymentManualDeductionReason.Other}>
                        {t(`appraisalMeeting.stepPayment.deduction.reason.${ObjectivePaymentManualDeductionReason.Other}`)}
                      </Option>
                    </FormikSelect>
                  </FormItem>
                </div>
              </div>
            </>
          </PerModal>
        )
      }}
    </Formik>
  )
}
